import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Col, Grid, message, Modal, Row } from 'antd';
import { Formik } from 'formik';
import { omit, pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type BankDetailsWithPermissionsDataFragment } from '../../../api/fragments/BankDetailsWithPermissionsData';
import {
    DeleteBankDocument,
    type DeleteBankMutation,
    type DeleteBankMutationVariables,
} from '../../../api/mutations/deleteBank';
import {
    UpdateBankDocument,
    type UpdateBankMutation,
    type UpdateBankMutationVariables,
} from '../../../api/mutations/updateBank';
import {
    UpdateBankAvailableFinanceProductTypesDocument,
    type UpdateBankAvailableFinanceProductTypesMutation,
    type UpdateBankAvailableFinanceProductTypesMutationVariables,
} from '../../../api/mutations/updateBankAvailableFinanceProductTypes';
import {
    UpdateBmwFsBankIntegrationSettingDocument,
    type UpdateBmwFsBankIntegrationSettingMutation,
    type UpdateBmwFsBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateBmwFsBankIntegrationSetting';
import {
    UpdateDbsBankIntegrationSettingDocument,
    type UpdateDbsBankIntegrationSettingMutation,
    type UpdateDbsBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateDbsBankIntegrationSetting';
import {
    UpdateEmailBankIntegrationSettingDocument,
    type UpdateEmailBankIntegrationSettingMutation,
    type UpdateEmailBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateEmailBankIntegrationSetting';
import {
    UpdateEnbdBankIntegrationSettingDocument,
    type UpdateEnbdBankIntegrationSettingMutation,
    type UpdateEnbdBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateEnbdBankIntegrationSetting';
import {
    UpdateHlfBankIntegrationSettingDocument,
    type UpdateHlfBankIntegrationSettingMutation,
    type UpdateHlfBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateHlfBankIntegrationSetting';
import {
    UpdateHlfBankV2IntegrationSettingDocument,
    type UpdateHlfBankV2IntegrationSettingMutation,
    type UpdateHlfBankV2IntegrationSettingMutationVariables,
} from '../../../api/mutations/updateHlfBankV2IntegrationSetting';
import {
    UpdateMaybankIntegrationSettingDocument,
    type UpdateMaybankIntegrationSettingMutation,
    type UpdateMaybankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateMaybankIntegrationSetting';
import {
    UpdateUobBankIntegrationSettingDocument,
    type UpdateUobBankIntegrationSettingMutation,
    type UpdateUobBankIntegrationSettingMutationVariables,
} from '../../../api/mutations/updateUobBankIntegrationSetting';
import {
    BankIntegrationProvider,
    type BmwFsBankIntegration,
    type DbsBankIntegration,
    type EmailBankIntegration,
    type EnbdBankIntegration,
    type HlfBankIntegration,
    type HlfBankV2IntegrationSettings,
    type MaybankIntegration,
    type UobBankIntegration,
} from '../../../api/types';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import {
    type BankAvailableFinanceProductTypesValues,
    type BankFormValues,
    BankIntegration,
    type BankIntegrationValues,
    EditBankForm,
    FinanceProductType,
    useBankAvailableFinanceProductTypesValidation,
    useBankFormValidator,
} from './BankForm';
import useBankIntegrationFormValidation from './BankForm/useBankIntegrationFormValidation';
import useHandleEnbdAssets from './BankForm/useHandleEnbdAssets';
import useHandleBankAssets from './useHandleBankAssets';

export type BankDetailsInnerPageProps = { bank: BankDetailsWithPermissionsDataFragment };

const BankDetailsInnerPage = ({ bank }: BankDetailsInnerPageProps) => {
    const { t } = useTranslation(['bankDetails', 'financeProductDetails']);
    const bankId = bank.id;

    const apolloClient = useApolloClient();

    const { updated, offset } = useFormattedSimpleVersioning({ versioning: bank.versioning, moduleId: bank.moduleId });

    const handleBankAssets = useHandleBankAssets();
    const handleEnbdAssets = useHandleEnbdAssets();
    const screens = Grid.useBreakpoint();
    const navigate = useNavigate();
    const onSubmit = useHandleError<BankFormValues>(
        async values => {
            const { logo, font, ...settings } = values;

            // submitting message
            message.loading({
                content: t('bankDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<UpdateBankMutation, UpdateBankMutationVariables>({
                    mutation: UpdateBankDocument,
                    variables: { bankId, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // handle bank asset upload/delete
            await handleBankAssets(bankId, values, bank);

            // inform about success
            message.success({
                content: t('bankDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/banks');
        },
        [apolloClient, bank, bankId, handleBankAssets, navigate, t]
    );

    const onSubmitAvailableFinanceProductTypes = useHandleError<BankAvailableFinanceProductTypesValues>(
        async settings => {
            // submitting message
            message.loading({
                content: t('bankDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit update
            await apolloClient
                .mutate<
                    UpdateBankAvailableFinanceProductTypesMutation,
                    UpdateBankAvailableFinanceProductTypesMutationVariables
                >({
                    mutation: UpdateBankAvailableFinanceProductTypesDocument,
                    variables: { bankId, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('bankDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/banks');
        },
        [apolloClient, bankId, navigate, t]
    );
    const onSubmitIntegration = useHandleError<BankIntegrationValues>(
        async values => {
            const { provider, ...settings } = values;

            // submitting message
            message.loading({
                content: t('bankDetails:messages.updateBankIntegrationsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            switch (provider) {
                case BankIntegrationProvider.Email: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateEmailBankIntegrationSettingMutation,
                            UpdateEmailBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateEmailBankIntegrationSettingDocument,
                            variables: { bankId, settings: settings as EmailBankIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case BankIntegrationProvider.Hlf: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateHlfBankIntegrationSettingMutation,
                            UpdateHlfBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateHlfBankIntegrationSettingDocument,
                            variables: { bankId, settings: settings as HlfBankIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case BankIntegrationProvider.Hlfv2: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateHlfBankV2IntegrationSettingMutation,
                            UpdateHlfBankV2IntegrationSettingMutationVariables
                        >({
                            mutation: UpdateHlfBankV2IntegrationSettingDocument,
                            variables: { bankId, settings: settings as HlfBankV2IntegrationSettings },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case BankIntegrationProvider.Uob: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateUobBankIntegrationSettingMutation,
                            UpdateUobBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateUobBankIntegrationSettingDocument,
                            variables: { bankId, settings: settings as UobBankIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case BankIntegrationProvider.Dbs: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateDbsBankIntegrationSettingMutation,
                            UpdateDbsBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateDbsBankIntegrationSettingDocument,
                            variables: { bankId, settings: settings as DbsBankIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });
                    break;
                }

                case BankIntegrationProvider.BmwFs: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateBmwFsBankIntegrationSettingMutation,
                            UpdateBmwFsBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateBmwFsBankIntegrationSettingDocument,
                            variables: { bankId, settings: settings as BmwFsBankIntegration },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    break;
                }

                case BankIntegrationProvider.Maybank: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateMaybankIntegrationSettingMutation,
                            UpdateMaybankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateMaybankIntegrationSettingDocument,
                            variables: {
                                bankId,
                                settings: omit(['generatedId', 'generatedSecrets'], settings) as MaybankIntegration,
                            },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    break;
                }

                case BankIntegrationProvider.Enbd: {
                    // submit update
                    await apolloClient
                        .mutate<
                            UpdateEnbdBankIntegrationSettingMutation,
                            UpdateEnbdBankIntegrationSettingMutationVariables
                        >({
                            mutation: UpdateEnbdBankIntegrationSettingDocument,
                            variables: {
                                bankId,
                                settings: omit(['stampRight', 'stampBottom'], settings) as EnbdBankIntegration,
                            },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // submit update
                    await handleEnbdAssets(
                        bankId,
                        settings as EnbdBankIntegration,
                        bank.integration.__typename === 'EnbdBankIntegration' ? bank.integration : undefined
                    );

                    break;
                }

                default:
                    break;
            }

            // inform about success
            message.success({
                content: t('bankDetails:messages.updateBankIntegrationSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate('/admin/banks');
        },
        [apolloClient, bank.integration, bankId, handleEnbdAssets, navigate, t]
    );

    const deleteBank = useCallback(() => {
        Modal.confirm({
            title: t('bankDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('bankDetails:deleteModal.content'),
            okText: t('bankDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('bankDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('bankDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient
                        .mutate<DeleteBankMutation, DeleteBankMutationVariables>({
                            mutation: DeleteBankDocument,
                            variables: { bankId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    // show success
                    message.success({
                        content: t('bankDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/banks');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, bankId, navigate, t]);

    const initialValues = useMemo(
        (): BankFormValues => ({
            ...pick(
                [
                    'displayName',
                    'legalName',
                    'moduleId',
                    'email',
                    'color',
                    'order',
                    'remoteFlowAcknowledgmentInfo',
                    'financingDisclaimer',
                    'submissionApprovalModuleId',
                    'reSubmissionApprovalModuleId',
                    'sendEmailToCustomer',
                    'sendPDFToCustomer',
                    'isActive',
                    'font',
                    'logo',
                    'hasVSOUpload',
                    'hasUploadDocuments',
                    'estFee',
                    'allowVariantCodeMapping',
                    'showCommentsField',
                ],
                bank
            ),
        }),
        [bank]
    );

    const initialAvailableFinanceProductTypesValues = useMemo(
        (): BankAvailableFinanceProductTypesValues => ({
            ...pick(['availableFinanceProductTypes', 'showMarginOfFinance'], bank),
        }),
        [bank]
    );

    const initialIntegrationValues = useMemo((): BankIntegrationValues => bank.integration, [bank]);

    const validate = useBankFormValidator(true);
    const validateAvailableFinanceProductTypes = useBankAvailableFinanceProductTypesValidation();
    const validateIntegration = useBankIntegrationFormValidation(initialIntegrationValues.provider);

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(bank.permissions, [permissionKind.updateBank]),
            hasPermissions(bank.permissions, [permissionKind.deleteBank]),
        ],
        [bank]
    );

    return (
        <ConsolePageWithHeader
            extra={
                bank &&
                hasDeletePermission && (
                    <Button onClick={deleteBank} danger>
                        {t('bankDetails:actions.delete')}
                    </Button>
                )
            }
            onBack={() => navigate('/admin/banks')}
            title={screens.md ? t('bankDetails:title', { name: bank.displayName }) : bank.displayName}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                        {({ handleSubmit }) => (
                            <Form id="bankForm" name="bankForm" onSubmitCapture={handleSubmit}>
                                <FormAutoTouch />
                                <EditBankForm
                                    bank={bank}
                                    disabled={!hasUpdatePermission}
                                    offset={offset}
                                    updated={updated}
                                />
                            </Form>
                        )}
                    </Formik>
                </Col>
                <Col span={24}>
                    <Formik
                        initialValues={initialAvailableFinanceProductTypesValues}
                        onSubmit={onSubmitAvailableFinanceProductTypes}
                        validate={validateAvailableFinanceProductTypes}
                        validateOnChange
                    >
                        {({ handleSubmit }) => (
                            <Form
                                id="availableFinanceProductTypesForm"
                                name="availableFinanceProductTypesForm"
                                onSubmitCapture={handleSubmit}
                            >
                                <FinanceProductType bank={bank} disabled={!hasUpdatePermission} isUpdate />
                            </Form>
                        )}
                    </Formik>
                </Col>
                <Col span={24}>
                    <Formik
                        initialValues={initialIntegrationValues}
                        onSubmit={onSubmitIntegration}
                        validate={validateIntegration}
                    >
                        {({ handleSubmit }) => (
                            <Form id="bankIntegrationForm" name="bankIntegrationForm" onSubmitCapture={handleSubmit}>
                                <BankIntegration
                                    disabled={!hasUpdatePermission}
                                    provider={initialIntegrationValues.provider}
                                    isUpdate
                                />
                            </Form>
                        )}
                    </Formik>
                </Col>
            </Row>
        </ConsolePageWithHeader>
    );
};

export default BankDetailsInnerPage;
